import React from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

interface LoadingIndicatorProps {
  /**
   * Text to announce to screen readers
   */
  message?: string;
  /**
   * Size of the circular progress indicator
   */
  size?: number;
  /**
   * Show text visually alongside the spinner
   */
  showText?: boolean;
  /**
   * ARIA live region politeness
   */
  ariaLive?: 'polite' | 'assertive';
  /**
   * Whether this is a full-page loader
   */
  fullPage?: boolean;
}

/**
 * Accessible loading indicator with ARIA live region support
 * Announces loading state to screen readers
 */
const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  message = 'Loading...',
  size = 40,
  showText = false,
  ariaLive = 'polite',
  fullPage = false,
}) => {
  const content = (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
      role="status"
      aria-live={ariaLive}
      aria-busy="true"
    >
      <CircularProgress size={size} aria-hidden="true" />
      {showText ? (
        <Typography variant="body1" component="p">
          {message}
        </Typography>
      ) : (
        <Typography sx={visuallyHidden}>{message}</Typography>
      )}
    </Box>
  );

  if (fullPage) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        {content}
      </Box>
    );
  }

  return content;
};

export default LoadingIndicator;